import { TwinEmployeeParametersDict } from '@teinor/erp/types/company/userInner/employeeParameter'
import { ButtonRedSecondary, ButtonSecondary } from '../../../../../../../../baseComponents/Button'
import LoadingSpinner from '../../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { LittleModalCreateEdit } from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import ModalDeleteFilled from '../../../../../../../../baseComponents/ModalsLayouts/ModalDeleteFilled'
import { ModalEditComponentProps } from '../../../../../../../../baseComponents/ModalsLayouts/types'
import TwinTrans from '../../../../../../../../baseComponents/TwinTrans'
import { FormElement, FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { valueOrDefaultValue } from '../../../../../../../../utils/globals/valueOrDefaultValue'
import { SetEditDeleteModalTy } from '../../../../../../../../utils/hooks/useEditDeleteModal'
import { useFetchText } from '../../../../../../../../utils/hooks/useFetchText'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'

interface ConfigEmployeeEmailCustomRenderProps {
    setOpenModal: SetEditDeleteModalTy
    employeeParameters: TwinDictionary | null
 }

export const ConfigEmployeeEmailCustomRender: React.FC<ConfigEmployeeEmailCustomRenderProps> = ({ setOpenModal, employeeParameters }: ConfigEmployeeEmailCustomRenderProps) => {
    if (!employeeParameters) {
        return <LoadingSpinner />
    }
    if (employeeParameters?.emailManagerEmail) {
        return <ConfiguratedEmployeeEmail setOpenModal={setOpenModal} email={employeeParameters?.emailManagerEmail} />
    }
    return (
        <ConfigureEmployeeEmail setOpenModal={setOpenModal} />
    )
}

interface ConfiguratedEmployeeEmailProps {
    email: string
    setOpenModal: SetEditDeleteModalTy
}

const ConfiguratedEmployeeEmail: React.FC<ConfiguratedEmployeeEmailProps> = ({ email, setOpenModal }) => {
    return (
        <div className='gap-x-20 gap-y-10 flex flex-row items-center flex-wrap'>
            <div className='flex gap-x-5'>
                <TwinTrans transKey={'EmployeeEmailConfigured'}>Email configurado:</TwinTrans>
                <span>{email}</span>
            </div>
            <ButtonRedSecondary className='w-fit' type='button' onClick={() => setOpenModal({ type: 'delete', allRowData: {}})} key={'deleteEmployeeEmailConfig'}><TwinTrans transKey={'deleteEmployeeEmailConfig'}>Borrar configuración de email</TwinTrans></ButtonRedSecondary>
            <ButtonSecondary className='w-fit' type='button' onClick={() => setOpenModal({
                type: 'edit', allRowData: {} })} key={'adjustEmployeeEmailConfig'}><TwinTrans transKey={'adjustEmployeeEmailConfig'}>Modificar configuración de email</TwinTrans></ButtonSecondary>
        </div>
    )
}

interface ConfigureEmployeeEmailProps {
    setOpenModal: SetEditDeleteModalTy
}
const ConfigureEmployeeEmail: React.FC<ConfigureEmployeeEmailProps> = ({ setOpenModal }) => {
    return (
        <div className='gap-10 flex flex-row items-center'>
            <span><TwinTrans transKey={'EmployeeEmailParametersConfig'}>Configurar parámetros de email del empleado</TwinTrans>:</span>
            <ButtonSecondary className='w-fit' type='button' onClick={() => setOpenModal({ type: 'create', allRowData: {}})} key={'configEmployeeEmail'}><TwinTrans transKey={'Config'}>Configurar</TwinTrans></ButtonSecondary>
        </div>
    )
}

interface ModalDeleteEmployeeEmailConfigQuoteProps extends ModalEditComponentProps { }

export const ModalDeleteEmployeeEmailConfigQuote: React.FC<ModalDeleteEmployeeEmailConfigQuoteProps> = ({ setOpened, onSubmit }) => {
    const { t } = useTwinTranslation()
    const employeeParametersToDelete: TwinEmployeeParametersDict  = [ 'emailManagerPassword', 'emailManagerSecure', 'emailManagerEmail', 'emailManagerHost', 'emailManagerPort', 'emailNameFrom']
    const { handleFetchText } = useFetchText({
        onSubmit,
        url: '/api/app/config/employee/deleteEmployeeParameters',
        data: {
            employeeParameters: employeeParametersToDelete
        }
    })

    return (
        <ModalDeleteFilled opened={true} setOpened={setOpened} onCancel={() => setOpened(null)} onAccept={handleFetchText} translations={{
            title: t('sureDeleteEmployeeEmailConfig', '¿Seguro que quieres borrar la configuración de email?'),
            subtitle: t('YouWillBeAbleToConfigItAgain', 'Podrás volver a configurarla de nuevo'),
            buttonAccept: t('delete', 'Borrar'),
        }} />
    )
}

interface ModalEmployeeEmailParametersConfigProps extends ModalEditComponentProps { }

export const ModalEmployeeEmailParametersConfig: React.FC<ModalEmployeeEmailParametersConfigProps> = ({ allRowData,  setOpened, onSubmit, userPermissions }: ModalEmployeeEmailParametersConfigProps) => {
    const { t } = useTwinTranslation()
    const onOff = {
        'on': { name: t('on', 'Activado') },
        'off': { name: t('disabledS', 'Desactivado') },
    }
    const passwordElement: FormElement<keyof TwinEmployeeParametersDict>[] = []
    if(!allRowData){
            passwordElement.push({
                name: 'emailManagerPassword',
                label: t('emailManagerPassword', 'Contraseña del email del empleado'),
                component: 'InputPasswordWithLabel',
                type: 'password',
                required: true
            },)
    }
    const fields: FormRender<TwinEmployeeParametersDict> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'emailManagerEmail',
                    label: t('emailManagerEmail', 'Email del empleado'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                ...passwordElement,
                {
                    name: 'emailManagerHost',
                    label: t('emailManagerHost', 'Host del email del empleado'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                {
                    name: 'emailManagerPort',
                    label: t('emailManagerPort', 'Puerto del email del empleado'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    defaultValue: '587',
                    required: true
                },
                {
                    name: 'emailNameFrom',
                    label: t('emailNameFrom', 'Nombe del remitente'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'emailManagerSecure',
                    label: t('emailManagerSecure', 'Seguridad del email del empleado'),
                    component: 'CustomSelect',
                    items: onOff,
                    value: 'off',
                    required: true,
                },
            ]
        },
    ]
    if (allRowData && userPermissions) {
        const parsedFields = valueOrDefaultValue(fields, userPermissions, allRowData)
        return (
            <LittleModalCreateEdit className={'flex flex-col'} fields={parsedFields} setOpened={setOpened} url={'/api/app/config/employee/updateEmployeeParameters'} translations={{
                title: t('parameters', 'Parámetros'),
                button: t('save', 'Guardar')
            }} haveButtonPermissions={true} onSubmit={onSubmit} />
        )
    }
    return (
        <LittleModalCreateEdit className={'flex flex-col'} fields={fields} setOpened={setOpened} url={'/api/app/config/employee/updateEmployeeParameters'} translations={{
        title: t('parameters', 'Parámetros'),
        button: t('save', 'Guardar')
    }} haveButtonPermissions={true} onSubmit={onSubmit} />
    )
}